// Shared docx rendering for the headhunting preview modals (ES-1658 lot B, S3).
// Used by both Index.vue and Candidate.vue so the two modals stay identical.
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// Detect a docx document: mime type when known, else the url extension
// (the headhunting candidate list payload has no mime_type, only the CDN url).
// `application/msword` (.doc) is never docx — it stays on the today fallback.
export function isDocx(url, mimeType) {
  if (mimeType) return mimeType === DOCX_MIME;
  if (!url) return false;
  return /\.docx(\?|$)/i.test(url);
}

// Legacy binary .doc (ES-1658 QA3): docx-preview cannot read it; the API
// renders it to PDF (JOINT-CONTRACT-3). Same detection rule as isDocx; the
// extension test is anchored so `.docx` never matches.
const DOC_MIME = "application/msword";
export function isDoc(url, mimeType) {
  if (mimeType) return mimeType === DOC_MIME;
  if (!url) return false;
  return /\.doc(\?|$)/i.test(url);
}

// PDF rendering of a .doc as an object URL for the existing iframe. Any
// non-2xx or network error rejects: the caller shows today's fallback.
// The caller owns the URL and revokes it (revokeDocPreviewUrl).
export function fetchDocPreviewUrl(http, documentId) {
  return http
    .get(`/enroll_admin/documents/${documentId}/preview`, {
      responseType: "arraybuffer",
      headers: { Accept: "application/json" }
    })
    .then(({ data }) =>
      URL.createObjectURL(new Blob([data], { type: "application/pdf" }))
    );
}

export function revokeDocPreviewUrl(url) {
  if (url) URL.revokeObjectURL(url);
}

// Bytes through the authenticated admin proxy (endpoint 5), never the CDN:
// private candidate documents have no cdn, and this keeps parity with S1/S2.
// Accept: application/json so a miss renders JSON 404, not an HTML debug page.
export function fetchDocumentBytes(http, documentId) {
  return http.get(`/enroll_admin/documents/${documentId}/content`, {
    responseType: "arraybuffer",
    headers: { Accept: "application/json" }
  });
}

// Render docx bytes into `el`, inside a shadow root so Word's own CSS
// cannot leak into the admin panel's Bootstrap styles (UI-CONTRACT D4).
// `import()` keeps docx-preview out of the app's main bundle.
export async function renderDocxInto(el, arrayBuffer) {
  // Subpath import: docx-preview's package.json only declares an "exports"
  // map (no "main"), which webpack 4's resolver does not understand.
  const { renderAsync } = await import(
    /* webpackChunkName: "docx-preview" */ "docx-preview/dist/docx-preview.js"
  );
  const root = el.shadowRoot || el.attachShadow({ mode: "open" });
  revokeBlobUrls(root);
  root.innerHTML = "";
  const host = document.createElement("div");
  root.appendChild(host);
  await renderAsync(arrayBuffer, host, undefined, {
    ignoreWidth: true,
    ignoreHeight: true,
    breakPages: true,
    ignoreFonts: true,
    renderComments: false,
    // Embedded HTML parts (altChunk) would render as a same-origin iframe
    // srcdoc: candidate-supplied HTML running in the admin session.
    renderAltChunks: false,
    inWrapper: true
  });
  neutraliseLinks(root);
}

// The document is candidate-supplied: a hyperlink could carry a `javascript:`
// URL that would run in the admin session on click (a shadow root isolates
// CSS, not scripts). Keep only http(s)/mailto links, opened in a new tab.
const SAFE_HREF = /^(https?:|mailto:)/i;
export function neutraliseLinks(root) {
  root.querySelectorAll("a[href]").forEach(a => {
    const href = (a.getAttribute("href") || "").trim();
    if (href.startsWith("#")) return;
    if (!SAFE_HREF.test(href)) {
      a.removeAttribute("href");
      return;
    }
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
}

// Release a rendered document so a stale render never flashes on reopen.
export function clearDocx(el) {
  if (el && el.shadowRoot) {
    revokeBlobUrls(el.shadowRoot);
    el.shadowRoot.innerHTML = "";
  }
}

// docx-preview creates one object URL per embedded image and never revokes
// it: release them before a render is cleared or replaced.
const BLOB_URL_IN_CSS = /url\(\s*["']?(blob:[^"')]+)["']?\s*\)/gi;
function revokeBlobUrls(root) {
  const urls = new Set();
  root.querySelectorAll("img[src]").forEach(img => {
    const src = img.getAttribute("src") || "";
    if (src.startsWith("blob:")) urls.add(src);
  });
  const cssTexts = [];
  root
    .querySelectorAll("[style]")
    .forEach(n => cssTexts.push(n.getAttribute("style") || ""));
  root
    .querySelectorAll("style")
    .forEach(n => cssTexts.push(n.textContent || ""));
  cssTexts.forEach(text => {
    let match;
    BLOB_URL_IN_CSS.lastIndex = 0;
    while ((match = BLOB_URL_IN_CSS.exec(text)) !== null) urls.add(match[1]);
  });
  urls.forEach(url => URL.revokeObjectURL(url));
}
